// frontend/src/utils/intents/relationshipIntentUtils.js
// Owns: semantic first-order relationship recognition + structured write admission

import axios from '../../api/axiosInstance';
import { API_BASE } from '../../api/config';
import { debugLog } from '../logging/debugLog';
import {
  getRelationshipDeclarationTerms,
  normalizeRelationshipTerm as normalizeRelationshipTermFromOntology,
} from '../relationships/relationshipOntology';

const RELATIONSHIP_CUE_TERMS = getRelationshipDeclarationTerms();
const RELATIONSHIP_TERMS = [...new Set(
  RELATIONSHIP_CUE_TERMS
    .map((term) => normalizeRelationshipTermFromOntology(term))
    .filter(Boolean)
)];

export function normalizeRelationshipTerm(value = '') {
  return normalizeRelationshipTermFromOntology(value);
}

function normalizePersonName(value = '') {
  return String(value)
    .trim()
    .split(' ')
    .filter(Boolean)
    .join(' ')
    .replace(/[.,!?]+$/, '');
}

function normalizeComparableName(value = '') {
  return normalizePersonName(value).toLowerCase();
}

function getRelationshipRecordName(record = {}) {
  return normalizePersonName(
    [record?.first_name || record?.firstName, record?.last_name || record?.lastName]
      .filter(Boolean)
      .join(' ')
  );
}

function getRelationshipRecordRole(record = {}) {
  return normalizeRelationshipTerm(
    record?.relationship ||
      record?.relationship_type ||
      record?.relationshipType ||
      ''
  );
}

function buildRelationshipIdentityKey(record = {}) {
  const entityId = String(record?.entityId || '').trim();
  if (entityId) return `id:${entityId}`;

  const name = normalizeComparableName(getRelationshipRecordName(record));
  const role = getRelationshipRecordRole(record);
  if (!name) return '';

  return `name:${name}::${role || 'unknown'}`;
}

function getCurrentRelationshipMatches({
  personName = '',
  relationship = '',
  relationships = [],
} = {}) {
  const candidateName = normalizeComparableName(personName);
  const candidateRole = normalizeRelationshipTerm(relationship);

  if (!candidateName || !candidateRole || !Array.isArray(relationships)) {
    return { nameMatches: [], exactMatches: [] };
  }

  const candidateHasFullName = candidateName.includes(' ');
  const activeRelationships = relationships.filter(
    (record) => record?.isActive !== false
  );

  const nameMatches = activeRelationships.filter((record) => {
    const recordName = normalizeComparableName(getRelationshipRecordName(record));
    if (!recordName) return false;

    if (candidateHasFullName) {
      return recordName === candidateName;
    }

    const recordFirstName = normalizeComparableName(
      record?.first_name || record?.firstName || ''
    );

    return recordFirstName === candidateName;
  });

  const authoritativeNameMatches = preferIdentityBoundRelationships(nameMatches);
  const exactMatches = authoritativeNameMatches.filter(
    (record) => getRelationshipRecordRole(record) === candidateRole
  );

  return {
    nameMatches: authoritativeNameMatches,
    exactMatches,
  };
}

function preferIdentityBoundRelationships(records = []) {
  const identityBound = records.filter((record) =>
    Boolean(String(record?.entityId || '').trim())
  );

  return identityBound.length ? identityBound : records;
}

function dedupeRelationshipEntities(records = []) {
  const byIdentity = new Map();

  preferIdentityBoundRelationships(records).forEach((record) => {
    const key = buildRelationshipIdentityKey(record);
    if (!key || byIdentity.has(key)) return;
    byIdentity.set(key, record);
  });

  return [...byIdentity.values()];
}

function arbitrateFirstOrderRelationshipDeclaration(
  declaration = null,
  relationships = []
) {
  if (!declaration) return null;

  const { nameMatches, exactMatches } = getCurrentRelationshipMatches({
    personName: declaration.personName,
    relationship: declaration.relationship,
    relationships,
  });

  const exactEntities = dedupeRelationshipEntities(exactMatches);
  if (exactEntities.length === 1) {
    const existing = exactEntities[0];

    return {
      ...declaration,
      entityId: String(existing?.entityId || '').trim() || null,
      alreadyKnown: true,
      relationshipDisposition: 'existing_exact',
      requiresConfirmation: false,
      writeMode: 'already_known',
      source: `${declaration.source || 'relationship_declaration'}_existing`,
    };
  }

  if (exactEntities.length > 1) {
    return {
      ...declaration,
      relationshipDisposition: 'ambiguous_identity',
      requiresClarification: true,
      requiresConfirmation: false,
      writeMode: 'clarify_before_write',
    };
  }

  const namedEntities = dedupeRelationshipEntities(nameMatches);
  if (namedEntities.length > 1) {
    return {
      ...declaration,
      relationshipDisposition: 'ambiguous_identity',
      requiresClarification: true,
      requiresConfirmation: false,
      writeMode: 'clarify_before_write',
    };
  }

  if (namedEntities.length === 1) {
    const existingEntity = namedEntities[0];

    return {
      ...declaration,
      entityId: String(existingEntity?.entityId || '').trim() || null,
      relationshipDisposition: 'known_entity_new_relationship',
      requiresConfirmation: true,
      writeMode: 'confirm_before_write',
    };
  }

  return {
    ...declaration,
    relationshipDisposition: 'new_entity_relationship',
    requiresConfirmation: true,
    writeMode: 'confirm_before_write',
  };
}

function normalizeCueText(value = '') {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[‘’]/g, "'")
    .toLowerCase();
}

function tokenizeCueText(value = '') {
  const normalized = normalizeCueText(value);
  const tokens = [];
  let current = '';

  for (const char of normalized) {
    const isLetterOrNumber = char.toLowerCase() !== char.toUpperCase() || (char >= '0' && char <= '9');
    const keepJoiner = (char === "'" || char === '-') && current.length > 0;

    if (isLetterOrNumber || keepJoiner) {
      current += char;
      continue;
    }

    if (current) {
      tokens.push(current);
      current = '';
    }
  }

  if (current) tokens.push(current);
  return tokens;
}

function buildCueForms(value = '') {
  const normalized = normalizeCueText(value).trim();
  if (!normalized) return [];

  const forms = new Set([normalized]);

  if (normalized.endsWith("'s")) {
    forms.add(normalized.slice(0, -2));
  }

  if (normalized.endsWith('ies') && normalized.length > 3) {
    forms.add(`${normalized.slice(0, -3)}y`);
  }

  if (normalized.endsWith('s') && normalized.length > 2) {
    forms.add(normalized.slice(0, -1));
  }

  return [...forms];
}

function hasRelationshipRoleCue(message = '') {
  const messageTokens = tokenizeCueText(message);
  if (!messageTokens.length) return false;

  const messageForms = new Set(
    messageTokens.flatMap((token) => buildCueForms(token))
  );
  const normalizedMessage = ` ${normalizeCueText(message).split(' ').filter(Boolean).join(' ')} `;

  return RELATIONSHIP_CUE_TERMS.some((term) => {
    const normalizedTerm = normalizeCueText(term).trim();
    if (!normalizedTerm) return false;

    if (normalizedTerm.includes(' ')) {
      return normalizedMessage.includes(` ${normalizedTerm} `);
    }

    return buildCueForms(normalizedTerm).some((form) => messageForms.has(form));
  });
}

function buildRelationshipDeclaration({
  relationship,
  personName,
  source,
  confidence = 0.92,
  temporality = 'current',
  sourceText = null,
} = {}) {
  if (!relationship || !personName) return null;

  const normalizedRelationship = normalizeRelationshipTerm(relationship);
  const normalizedPersonName = normalizePersonName(personName);
  const normalizedTemporality = String(temporality || 'current').trim().toLowerCase();
  const normalizedConfidence = Number(confidence);

  if (!normalizedRelationship || !normalizedPersonName) return null;
  if (normalizedTemporality !== 'current') return null;
  if (!Number.isFinite(normalizedConfidence)) return null;

  return {
    intent: 'relationship_declaration',
    target: normalizedRelationship,
    relationship: normalizedRelationship,
    personName: normalizedPersonName,
    memoryName: normalizedPersonName,
    source,
    sourceText: sourceText || null,
    temporality: 'current',
    confidence: normalizedConfidence,
    requiresConfirmation: true,
    writeMode: 'confirm_before_write',
  };
}

export function buildConfirmedRelationshipWritePayload(intentInfo = {}) {
  const normalizedRelationship = normalizeRelationshipTerm(intentInfo?.relationship || '');
  const normalizedPersonName = normalizePersonName(intentInfo?.personName || '');

  if (!normalizedRelationship || !normalizedPersonName) {
    return null;
  }

  return {
    entityId: String(intentInfo?.entityId || '').trim() || null,
    first_name: normalizedPersonName,
    last_name: null,
    relationship: normalizedRelationship,
    source: 'conversation',
    isActive: true,
  };
}

export async function detectRelationshipDeclaration(message = '', relationships = []) {
  const trimmed = String(message || '').trim();
  if (!trimmed) return null;

  // This is only a cheap ontology-vocabulary gate. Sentence meaning is resolved
  // semantically by the backend extractor; no sentence-shape parsing lives here.
  if (!hasRelationshipRoleCue(trimmed)) {
    return null;
  }

  try {
    const response = await axios.post(
      `${API_BASE}/api/relationship-network/extract-first-order`,
      {
        message: trimmed,
        relationshipTerms: RELATIONSHIP_TERMS,
      }
    );

    const semanticDeclaration = response.data?.declaration || null;

    debugLog('🧭 [FirstOrderRelationship] Semantic extraction result:', {
      message: trimmed,
      declaration: semanticDeclaration,
    });

    if (!semanticDeclaration) return null;

    const declaration = buildRelationshipDeclaration({
      relationship: semanticDeclaration.relationship,
      personName: semanticDeclaration.personName,
      source: 'first_order_semantic_extractor',
      confidence:
        typeof semanticDeclaration.confidence === 'number'
          ? semanticDeclaration.confidence
          : 0.92,
      temporality: semanticDeclaration.temporality || 'current',
      sourceText: semanticDeclaration.sourceText || trimmed,
    });

    if (!declaration && semanticDeclaration.temporality === 'historical') {
      debugLog('🧭 [FirstOrderRelationship] Historical relationship observed; current write suppressed:', {
        personName: semanticDeclaration.personName || null,
        relationship: semanticDeclaration.relationship || null,
      });
    }

    const arbitratedDeclaration = arbitrateFirstOrderRelationshipDeclaration(
      declaration,
      relationships
    );

    debugLog('🧭 [FirstOrderRelationship] Canonical state arbitration:', {
      message: trimmed,
      personName: arbitratedDeclaration?.personName || null,
      relationship: arbitratedDeclaration?.relationship || null,
      entityId: arbitratedDeclaration?.entityId || null,
      disposition: arbitratedDeclaration?.relationshipDisposition || null,
      alreadyKnown: arbitratedDeclaration?.alreadyKnown === true,
      requiresClarification:
        arbitratedDeclaration?.requiresClarification === true,
      requiresConfirmation:
        arbitratedDeclaration?.requiresConfirmation === true,
    });

    return arbitratedDeclaration;
  } catch (err) {
    console.warn(
      '⚠️ [FirstOrderRelationship] Semantic extraction failed:',
      err.response?.data || err.message
    );
    return null;
  }
}
